import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PASSWORD_RULES = [
  { test: (p) => (p || "").length >= 8, get label() { return tr("At least 8 characters"); } },
  { test: (p) => /\d/.test(p || ""), get label() { return tr("At least 1 number"); } },
];

const PasswordInput = ({ value, onChange, placeholder, testid, error, autoFocus }) => {
  usePartnerTranslation();
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        autoFocus={autoFocus}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={cn(
          "humrahe-input !pe-10",
          error && "border-[#9B0000] focus:border-[#9B0000]",
        )}
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? tr("Hide password") : tr("Show password")}
        className="absolute end-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FAFAFB]"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const FieldLabel = ({
  children,
  error
}) => {
  usePartnerTranslation();
  return <label className={cn("text-xs font-semibold", error ? "text-[#9B0000]" : "text-[#0F1115]")}>
    {children}
  </label>;
};

export default function ChangePasswordPage() {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState("otp"); // otp | passwords | done
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpHint, setOtpHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const sendOtp = async () => {
    setBusy(true);
    setErrors({});
    try {
      const r = await axios.post(`${API}/account/send-change-password-otp`);
      setOtpSent(true);
      const devOtp = r.data?.dev_otp;
      if (devOtp) setOtpHint(devOtp);
      toast.success(tr("Code sent"), {
        description: devOtp
          ? tr("DEV OTP: {{value0}} — expires in 10 minutes. (No mailer wired yet)", { value0: devOtp })
          : tr("Check {{value0}}.", { value0: user?.email || "your email" }),
      });
    } catch {
      toast.error(tr("Couldn't send the code. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const continueToPasswords = () => {
    const code = (otp || "").trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setErrors({ otp: "Enter the 6-digit code." });
      return;
    }
    setErrors({});
    setStage("passwords");
  };

  const submitChange = async () => {
    const e = {};
    if (!currentPw) e.currentPw = tr("Enter your current password");
    PASSWORD_RULES.forEach((r) => {
      if (!r.test(newPw)) e.newPw = r.label;
    });
    if (newPw && newPw === currentPw)
      e.newPw = tr("New password must be different from the current one");
    if (newPw !== confirmPw) e.confirmPw = tr("Passwords don't match");
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      await axios.post(`${API}/account/change-password`, {
        otp,
        current_password: currentPw,
        new_password: newPw,
      });
      setStage("done");
      toast.success(tr("Password changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : "Couldn't change password.";
      if (/otp/i.test(msg)) setErrors({ otp: msg });
      else if (/current/i.test(msg)) setErrors({ currentPw: msg });
      else if (/different/i.test(msg)) setErrors({ newPw: msg });
      else setErrors({ form: msg });
      if (/expired|invalid otp/i.test(msg)) setStage("otp");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="change-password-page"
      className="mx-auto w-full max-w-[440px] flex-1 px-4 py-6 sm:py-10"
    >
      <button
        type="button"
        onClick={() => navigate("/account")}
        data-testid="change-pw-back"
        className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-[#6B7280] hover:text-[#0F1115]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />{tr("Back")}</button>
      <h1 className="text-2xl font-extrabold tracking-tight text-[#0F1115] sm:text-3xl">{tr("Change password")}</h1>
      <p className="mt-1 text-sm text-[#6B7280]">
        {stage === "otp"
          ? tr("We'll email you a 6-digit code.")
          : stage === "passwords"
            ? tr("Set a new password.")
            : ""}
      </p>

      {stage === "otp" && (
        <div className="mt-6 space-y-4" data-testid="change-pw-otp-stage">
          {!otpSent ? (
            <button
              type="button"
              onClick={sendOtp}
              disabled={busy}
              data-testid="change-pw-send-otp"
              className="humrahe-btn-primary inline-flex h-10 w-full items-center justify-center rounded-md px-5 text-sm font-semibold disabled:opacity-60 sm:w-auto"
            >
              {busy ? tr("Sending…") : tr("Send code to {{value0}}", { value0: user?.email || "my email" })}
            </button>
          ) : (
            <div className="space-y-2">
              <FieldLabel error={!!errors.otp}>{tr("6-digit code")}</FieldLabel>
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                data-testid="change-pw-otp-input"
                className={cn(
                  "humrahe-input text-center font-mono text-lg tracking-[0.5em]",
                  errors.otp && "border-[#9B0000]",
                )}
              />
              {errors.otp && (
                <p className="text-[11px] font-medium text-[#9B0000]">
                  {errors.otp}
                </p>
              )}
              {otpHint && (
                <p className="text-[11px] text-[#9BA1AB]">{tr("Dev mode — code is")}{" "}
                  <span className="font-mono text-[#0F1115]">{otpHint}</span>
                </p>
              )}
              <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={continueToPasswords}
                  data-testid="change-pw-otp-continue"
                  className="humrahe-btn-primary inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-semibold"
                >{tr("Continue")}</button>
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={busy}
                  data-testid="change-pw-resend"
                  className="text-xs font-semibold text-[#2476FF] hover:underline disabled:opacity-60"
                >
                  {busy ? tr("Resending…") : tr("Resend code")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {stage === "passwords" && (
        <div className="mt-6 space-y-4" data-testid="change-pw-passwords-stage">
          <div className="space-y-1.5">
            <FieldLabel error={!!errors.currentPw}>{tr("Current password")}</FieldLabel>
            <PasswordInput
              autoFocus
              value={currentPw}
              onChange={setCurrentPw}
              placeholder={tr("Your current password")}
              testid="change-pw-current"
              error={errors.currentPw}
            />
            {errors.currentPw && (
              <p className="text-[11px] font-medium text-[#9B0000]">
                {errors.currentPw}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <FieldLabel error={!!errors.newPw}>{tr("New password")}</FieldLabel>
            <PasswordInput
              value={newPw}
              onChange={setNewPw}
              placeholder={tr("At least 8 characters with 1 number")}
              testid="change-pw-new"
              error={errors.newPw}
            />
            <div className="flex flex-wrap gap-x-3 gap-y-1 ps-1 text-[11px]">
              {PASSWORD_RULES.map((r) => {
                const ok = r.test(newPw);
                return (
                  <span
                    key={r.label}
                    className={cn(
                      "inline-flex items-center gap-1.5",
                      ok ? "text-[#0F6B2B]" : "text-[#9BA1AB]",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-1.5 w-1.5 rounded-full",
                        ok ? "bg-[#0F6B2B]" : "bg-[#CFD2D8]",
                      )}
                    />
                    {r.label}
                  </span>
                );
              })}
            </div>
            {errors.newPw && (
              <p className="text-[11px] font-medium text-[#9B0000]">
                {errors.newPw}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <FieldLabel error={!!errors.confirmPw}>{tr("Confirm new password")}</FieldLabel>
            <PasswordInput
              value={confirmPw}
              onChange={setConfirmPw}
              placeholder={tr("Type it again")}
              testid="change-pw-confirm"
              error={errors.confirmPw}
            />
            {errors.confirmPw && (
              <p className="text-[11px] font-medium text-[#9B0000]">
                {errors.confirmPw}
              </p>
            )}
          </div>

          {errors.form && (
            <p className="text-[11px] font-medium text-[#9B0000]">{errors.form}</p>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={submitChange}
              disabled={busy}
              data-testid="change-pw-submit"
              className="humrahe-btn-primary inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-semibold disabled:opacity-60"
            >
              {busy ? tr("Changing…") : tr("Change password")}
            </button>
            <button
              type="button"
              onClick={() => setStage("otp")}
              className="text-xs font-semibold text-[#6B7280] hover:text-[#0F1115]"
              data-testid="change-pw-back-to-otp"
            >{tr("← Resend code")}</button>
          </div>
        </div>
      )}

      {stage === "done" && (
        <div
          className="mt-6 space-y-3"
          data-testid="change-pw-done-stage"
        >
          <p className="text-sm text-[#0F1115]">
            <span className="font-semibold">{tr("Password changed.")}</span>{" "}{tr("Sign in again on other devices to keep them in sync.")}</p>
          <button
            type="button"
            onClick={() => navigate("/account")}
            data-testid="change-pw-done-back"
            className="humrahe-btn-primary inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-semibold"
          >{tr("Back to account management")}</button>
        </div>
      )}
    </main>
  );
}
